/**
 * PerspectiveCard — one card for one computed perspective on the
 * child-detail page (`/children/:id/perspectives`).
 *
 * Renders differently based on `visual_hint`:
 *   - `dot_grid`: inline EighteenSummersDotGrid (only for eighteen_summers)
 *   - `bar`: a number + progress meter (like the stat bars on KidCard)
 *   - `number`: a big number, no meter — for discrete counters
 *     (Christmases, piggyback years)
 *   - `meter`: a percent-style meter — used by pct_facetime_used
 *
 * The pin button is always present in the top-right and toggles via
 * the caller-provided `onPinToggle` callback (wired to the optimistic
 * mutation in `useUpdateChildPreferences`).
 *
 * Expired perspectives get a muted visual treatment + an "expired"
 * badge. The caller decides whether to render them at all — we
 * just honor the flag.
 */

import type { ComputedPerspective } from "@/features/perspectives/usePerspectives";
import {
  ConfidenceTilde,
  EighteenSummersDotGrid,
  PinButton,
} from "@/features/perspectives/primitives";

interface PerspectiveCardProps {
  perspective: ComputedPerspective;
  onPinToggle: () => void;
  pinDisabled?: boolean;
}

export function PerspectiveCard({
  perspective,
  onPinToggle,
  pinDisabled = false,
}: PerspectiveCardProps) {
  const isExpired = perspective.expired;

  return (
    <article
      className={[
        "relative flex flex-col gap-3 rounded-md border px-5 py-4",
        "bg-surface border-edge",
        isExpired && "opacity-60",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-serif text-[15px] text-ink leading-snug">
            {perspective.name}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isExpired && (
            <span className="font-sans text-[10px] uppercase tracking-[0.14em] font-semibold text-ink-muted border border-edge rounded-pill px-2 py-0.5">
              expired
            </span>
          )}
          <PinButton
            pinned={perspective.pinned}
            onToggle={onPinToggle}
            disabled={pinDisabled}
          />
        </div>
      </div>

      <PerspectiveValue perspective={perspective} />

      <div className="font-serif italic text-[13px] text-ink-muted">
        {perspective.caption}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// PerspectiveValue — the hint-specific rendering of the number
// ---------------------------------------------------------------------------

function PerspectiveValue({
  perspective,
}: {
  perspective: ComputedPerspective;
}) {
  const { value, visual_hint, confidence, unit, metadata } = perspective;
  const percentRemaining = extractPercentRemaining(perspective);

  if (visual_hint === "dot_grid") {
    const used = (metadata.used as number | undefined) ?? 0;
    const total = (metadata.total as number | undefined) ?? 18;
    return (
      <div className="flex flex-col gap-3">
        <EighteenSummersDotGrid used={used} total={total} />
        <div className="flex items-baseline gap-2">
          <div className="font-mono tabular-nums font-semibold text-ink text-[28px] md:text-[36px] leading-none">
            <ConfidenceTilde confidence={confidence} />
            {formatInteger(value)}
          </div>
          <div className="font-sans text-[13px] text-ink-muted">{unit}</div>
        </div>
      </div>
    );
  }

  if (visual_hint === "meter") {
    // Percent meter (pct_facetime_used). Render the value AS a
    // percentage bar since that's the point of the perspective.
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <div className="font-mono tabular-nums font-semibold text-ink text-[28px] md:text-[36px] leading-none">
            <ConfidenceTilde confidence={confidence} />
            {formatInteger(value)}
          </div>
          <div className="font-sans text-[13px] text-ink-muted">percent</div>
        </div>
        <div className="h-[6px] rounded-full bg-ink/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-amber-deep"
            style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
          />
        </div>
      </div>
    );
  }

  // `bar` and `number` differ only in whether they show a progress meter.
  const showMeter = visual_hint === "bar" && typeof percentRemaining === "number";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <div className="font-mono tabular-nums font-semibold text-ink text-[28px] md:text-[36px] leading-none">
          <ConfidenceTilde confidence={confidence} />
          {formatInteger(value)}
        </div>
        <div className="font-sans text-[13px] text-ink-muted">{unit}</div>
      </div>
      {showMeter && (
        <div className="h-[4px] rounded-full bg-ink/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-coral-deep"
            style={{ width: `${Math.max(0, Math.min(100, percentRemaining!))}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatInteger(value: number): string {
  if (Number.isInteger(value)) {
    return value.toLocaleString("en-US");
  }
  return value.toFixed(1);
}

function extractPercentRemaining(
  perspective: ComputedPerspective,
): number | undefined {
  const used = perspective.metadata.used;
  const total = perspective.metadata.total;
  if (typeof used !== "number" || typeof total !== "number" || total === 0) {
    return undefined;
  }
  return Math.round(((total - used) / total) * 100);
}
